import { PayloadAction } from "@reduxjs/toolkit";
import { call, put, select, takeLatest, takeLeading } from "redux-saga/effects";
import { API_URL } from "utils/constants";
import { request } from "utils/request";

import {
  CustomerDetailResponse,
  CustomerForm,
  CustomerListResponse,
  CustomerStatusFilter,
} from "../types";
import {
  selectCustomerForm,
  selectCustomersStatusFilter,
} from "./selector";
import { customersActions } from "./slice";
import { emptyCustomerDetails } from "./initialState";

let isFetchingList = false;

export function* handleGetCustomersList() {
  if (isFetchingList) return;
  isFetchingList = true;

  try {
    const statusFilter: CustomerStatusFilter = yield select(
      selectCustomersStatusFilter
    );

    const params = new URLSearchParams({
      page: "1",
      perPage: "20",
    });

    if (statusFilter !== "ALL") {
      params.set("status", statusFilter);
    }

    const response: CustomerListResponse = yield call(
      request,
      `${API_URL}/customer/list?${params.toString()}`,
      { method: "GET" }
    );

    yield put(
      customersActions.getCustomersListSuccess(response.data || [])
    );
  } catch (error) {
    yield put(
      customersActions.getCustomersListError((error as Error).message)
    );
  } finally {
    isFetchingList = false;
  }
}

export function* handleGetCustomerDetails(action: PayloadAction<string>) {
  try {
    const response: CustomerDetailResponse = yield call(
      request,
      `${API_URL}/customer/${action.payload}`,
      { method: "GET" }
    );

    yield put(
      customersActions.getCustomerDetailsSuccess(
        response.data || {
          ...emptyCustomerDetails,
          customerId: Number(action.payload),
        }
      )
    );
  } catch (error) {
    yield put(
      customersActions.getCustomerDetailsError((error as Error).message)
    );
  }
}

export function* handleAddCustomer() {
  const form: CustomerForm = yield select(selectCustomerForm);
  const customerName = form.customerName.trim();

  try {
    yield call(request, `${API_URL}/customer`, {
      method: "POST",
      body: JSON.stringify({
        customerShortName: customerName.split(/\s+/)[0] || customerName,
        customerName,
        category: form.category.trim(),
        contactPhone: form.contactPhone.trim(),
        contactPersonName: form.contactPerson.trim(),
        status: "active",
      }),
    });

    yield put(customersActions.addCustomerSuccess());
    yield put(customersActions.getCustomersList());
  } catch (error) {
    yield put(customersActions.addCustomerError((error as Error).message));
  }
}

export function* customersSaga() {
  yield takeLeading(
    customersActions.getCustomersList.type,
    handleGetCustomersList
  );
  yield takeLatest(
    customersActions.getCustomerDetails.type,
    handleGetCustomerDetails
  );
  yield takeLatest(customersActions.addCustomer.type, handleAddCustomer);
}

export default customersSaga;
